import { z } from 'zod'
import { Timestamp } from './common.js'

// User entity.
//
// Persisted shape (UserDoc) carries scrypt parameters alongside the hash so
// we can re-hash on parameter upgrades without invalidating sessions. The
// public projection (UserPublic) is what gets returned by /api/me and
// embedded in auth responses; password material never leaves the backend.

export const UserRole = z.enum(['user', 'admin'])
export type UserRole = z.infer<typeof UserRole>

export const UserIdentityProvider = z.enum(['apple'])
export type UserIdentityProvider = z.infer<typeof UserIdentityProvider>

export const USERNAME_PATTERN = /^[a-z0-9][a-z0-9_.-]{2,31}$/
export const RESERVED_USERNAME_PREFIX = 'apple_'
export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 256
const CONTROL_CHAR_PATTERN = /[\p{C}]/u

export const Username = z
  .string()
  .min(3)
  .max(32)
  .regex(USERNAME_PATTERN, 'username must be 3-32 chars: lowercase letters, digits, _ . -')
export const DisplayName = z.string().superRefine((value, ctx) => {
  const trimmed = value.trim()
  if (trimmed.length < 1) {
    ctx.addIssue({ code: z.ZodIssueCode.too_small, minimum: 1, type: 'string', inclusive: true })
  }
  if (trimmed.length > 64) {
    ctx.addIssue({ code: z.ZodIssueCode.too_big, maximum: 64, type: 'string', inclusive: true })
  }
  if (CONTROL_CHAR_PATTERN.test(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'display name cannot contain control characters',
    })
  }
})

export const Password = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH)

export const CoAdminRegion = z.object({
  province: z.string().optional(),
  city: z.string().optional(),
  district: z.string().optional(),
})
export type CoAdminRegion = z.infer<typeof CoAdminRegion>

export const UserPublic = z.object({
  id: z.string().uuid(),
  username: Username,
  display_name: DisplayName,
  role: UserRole,
  co_admin_region: CoAdminRegion.optional(),
  created_at: Timestamp,
  // Hiking-altitude user level (1-6), derived from the count of published routes
  // the user authored. Persisted on the doc and recomputed by services/user-level.ts.
  // Frontends map 1-6 to a named mountain + silhouette; the API only carries the number.
  // Optional on the type (old docs predate it); toPublic() always emits a value.
  level: z.number().int().min(1).max(6).optional(),
  avatar_url: z.string().optional(),
  profile_completed: z.boolean(),
})
export type UserPublic = z.infer<typeof UserPublic>

export const UserDoc = UserPublic.extend({
  profile_completed: z.boolean().optional(),
  // Avatar image (internal storage refs). avatar_url on UserPublic is derived
  // from these by toPublic() and is never persisted on the doc.
  avatar_blob_path: z.string().optional(),
  avatar_content_type: z.string().optional(),
  avatar_updated_at: Timestamp.optional(),
  // Legacy storage lookup key. Mirrors `id` so repos can do point reads.
  pk: z.string(),
  password_hash: z.string().optional(),
  password_salt: z.string().optional(),
  password_n: z.number().int().positive().optional(),
  password_r: z.number().int().positive().optional(),
  password_p: z.number().int().positive().optional(),
})
export type UserDoc = z.infer<typeof UserDoc>

export const UserProfileUpdateRequest = z
  .object({
    display_name: DisplayName.optional(),
  })
  .strict()
  .refine((value) => value.display_name !== undefined, {
    message: 'at least one profile field is required',
  })
export type UserProfileUpdateRequest = z.infer<typeof UserProfileUpdateRequest>

export const UserIdentityDoc = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  provider: UserIdentityProvider,
  subject: z.string().min(1).max(256),
  created_at: Timestamp,
  last_seen_at: Timestamp,
  // Apple refresh token, encrypted at rest (AES-256-GCM). Captured at exchange time
  // when the client sends `authorization_code`; replayed to Apple's /auth/revoke on
  // account deletion. Absent for identities created before this shipped (→ local unbind only).
  apple_refresh_token_enc: z.string().optional(),
  apple_refresh_token_updated_at: Timestamp.optional(),
})
export type UserIdentityDoc = z.infer<typeof UserIdentityDoc>

export function toPublic(doc: UserDoc): UserPublic {
  return {
    id: doc.id,
    username: doc.username,
    display_name: doc.display_name,
    role: doc.role,
    co_admin_region: doc.co_admin_region,
    created_at: doc.created_at,
    // Old docs predate the field; default to level 1 until reconciled.
    level: doc.level ?? 1,
    avatar_url:
      doc.avatar_blob_path && doc.avatar_updated_at
        ? `/api/users/${doc.id}/avatar?v=${Date.parse(doc.avatar_updated_at)}`
        : undefined,
    profile_completed: doc.profile_completed ?? !isReservedUsername(doc.username),
  }
}

export function isReservedUsername(username: string): boolean {
  return username.toLowerCase().startsWith(RESERVED_USERNAME_PREFIX)
}
